import * as XLSX from 'xlsx'
import {
  IMPLICIT_ATTITUDE_STATEMENTS,
  IMPLICIT_ATTRIBUTE_LABELS,
  IMPLICIT_DEADLINE_MS,
  advertisementLabel,
  attributeReactionStats,
  combinedReactionStats,
  implicitTestRuns,
  isValidImplicitTrial,
  reactionStats,
} from './implicitTestAnalysis'

function dateValue(value) {
  if (typeof value?.toDate === 'function') return value.toDate()
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

function mean(values, digits = 0) {
  const numeric = values.filter((value) => value !== null && value !== undefined && value !== '').map(Number).filter(Number.isFinite)
  if (!numeric.length) return null
  const result = numeric.reduce((sum, value) => sum + value, 0) / numeric.length
  return Number(result.toFixed(digits))
}

function percentage(value) {
  return Number.isFinite(value) ? value / 100 : null
}

function responseId(response) {
  return response.response_id || response.source_response_id || response.id || ''
}

function scoreValue(value) {
  if (value === null || value === undefined || value === '') return null
  const score = Number(value)
  return Number.isFinite(score) ? score : null
}

function makeSheet(rows, widths, percentageHeaders = []) {
  const sheet = XLSX.utils.json_to_sheet(rows, { cellDates: true })
  sheet['!cols'] = widths.map((wch) => ({ wch }))
  if (rows.length) {
    const headers = Object.keys(rows[0])
    sheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { c: 0, r: 0 }, e: { c: headers.length - 1, r: rows.length } }) }
    percentageHeaders.forEach((header) => {
      const column = headers.indexOf(header)
      if (column < 0) return
      for (let row = 1; row <= rows.length; row += 1) {
        const cell = sheet[XLSX.utils.encode_cell({ c: column, r: row })]
        if (cell && typeof cell.v === 'number') cell.z = '0.0%'
      }
    })
  }
  return sheet
}

function summaryRows(responses) {
  const activeResponses = responses.filter((response) => response.reportActive !== false)
  const activeRuns = activeResponses.flatMap(implicitTestRuns)
  const groups = activeRuns.reduce((result, run) => {
    const key = run.advertisement_id || 'unknown'
    result[key] = [...(result[key] || []), run]
    return result
  }, {})
  const makeRow = (scope, runs, participantCount) => {
    const stats = combinedReactionStats(runs)
    return {
      Kapsam: scope,
      'Aktif katılımcı': participantCount,
      'Reklam gösterimi': runs.length,
      'Toplam deneme': stats.trialCount,
      'Yanıtlanan': stats.answeredCount,
      'Zamanında yanıt': stats.validCount,
      'Zamanında yanıt oranı': percentage(stats.onTimePercent),
      'Ortalama geçerli tepki süresi (ms)': stats.validAverageRt,
      'Medyan geçerli tepki süresi (ms)': stats.medianRt,
      'Eşleşiyor oranı': percentage(stats.validMatchesPercent),
      'Ortalama motivasyon (1-5)': mean(runs.map((run) => run.questionnaire?.motivation), 2),
    }
  }
  return [
    makeRow('Tüm reklamlar', activeRuns, activeResponses.length),
    ...Object.entries(groups).map(([id, runs]) => makeRow(advertisementLabel(id), runs, new Set(runs.map((run) => run.responseId).filter(Boolean)).size || runs.length)),
  ]
}

export function exportImplicitTestExcel(responses) {
  const completedResponses = responses.filter((response) => response.status === 'completed' || implicitTestRuns(response).length)
  const normalized = completedResponses.map((response) => ({
    ...response,
    runs: implicitTestRuns(response).map((run) => ({ ...run, responseId: responseId(response) })),
  }))

  const participants = normalized.map((response) => {
    const stats = combinedReactionStats(response.runs)
    return {
      'Yanıt ID': responseId(response),
      'Katılımcı adı': response.respondent_name || '',
      BFRID: response.bfrid || response.participant_id || '',
      'Rapor durumu': response.reportActive === false ? 'Pasif' : 'Aktif',
      'Tamamlanma zamanı': dateValue(response.completed_at || response.updated_at) || '',
      'Reklam gösterimi': response.runs.length,
      'Toplam deneme': stats.trialCount,
      'Yanıtlanan': stats.answeredCount,
      'Zamanında yanıt': stats.validCount,
      'Zamanında yanıt oranı': percentage(stats.onTimePercent),
      'Ortalama geçerli tepki süresi (ms)': stats.validAverageRt,
      'Medyan geçerli tepki süresi (ms)': stats.medianRt,
      'Eşleşiyor oranı': percentage(stats.validMatchesPercent),
      'Ortalama motivasyon (1-5)': mean(response.runs.map((run) => run.questionnaire?.motivation), 2),
    }
  })

  const advertisementRows = normalized.flatMap((response) => response.runs.map((run) => {
    const stats = reactionStats(run.trials)
    const attitudes = run.questionnaire?.attitudes || {}
    return {
      'Yanıt ID': responseId(response),
      'Katılımcı adı': response.respondent_name || '',
      BFRID: response.bfrid || response.participant_id || '',
      'Rapor durumu': response.reportActive === false ? 'Pasif' : 'Aktif',
      Reklam: advertisementLabel(run.advertisement_id),
      'Toplam deneme': stats.trialCount,
      'Yanıtlanan': stats.answeredCount,
      'Zamanında yanıt': stats.validCount,
      'Zamanında yanıt oranı': percentage(stats.onTimePercent),
      'Ortalama geçerli tepki süresi (ms)': stats.validAverageRt,
      'Medyan geçerli tepki süresi (ms)': stats.medianRt,
      'Eşleşiyor sayısı': stats.validMatchesCount,
      'Eşleşiyor oranı': percentage(stats.validMatchesPercent),
      'Motivasyon (1-5)': Number(run.questionnaire?.motivation) || null,
      'Ana mesaj': run.questionnaire?.main_message || '',
      ...Object.fromEntries(Array.from({ length: 7 }, (_, index) => [`Tutum ${index + 1} (1-5)`, Number(attitudes[index]) || null])),
    }
  }))

  const attributeRows = normalized.flatMap((response) => response.runs.flatMap((run) => attributeReactionStats(run.trials).map((attribute) => ({
    'Yanıt ID': responseId(response),
    'Katılımcı adı': response.respondent_name || '',
    'Rapor durumu': response.reportActive === false ? 'Pasif' : 'Aktif',
    Reklam: advertisementLabel(run.advertisement_id),
    'Özellik no': attribute.attributeIndex,
    Özellik: attribute.attributeLabel,
    'Yunanca ifadeler': attribute.stimuli,
    'Toplam deneme': attribute.trialCount,
    'Zamanında yanıt': attribute.validCount,
    'Ortalama geçerli tepki süresi (ms)': attribute.validAverageRt,
    'Medyan geçerli tepki süresi (ms)': attribute.medianRt,
    'Eşleşiyor sayısı': attribute.validMatchesCount,
    'Eşleşiyor oranı': percentage(attribute.validMatchesPercent),
    'Zamanında yanıt oranı': percentage(attribute.onTimePercent),
  }))))

  const trialRows = normalized.flatMap((response) => response.runs.flatMap((run) => (run.trials || []).map((trial, index) => ({
    'Yanıt ID': responseId(response),
    'Katılımcı adı': response.respondent_name || '',
    'Rapor durumu': response.reportActive === false ? 'Pasif' : 'Aktif',
    Reklam: advertisementLabel(run.advertisement_id),
    'Deneme sırası': index + 1,
    'Özellik no': Number(trial.attributeIndex) || null,
    Özellik: IMPLICIT_ATTRIBUTE_LABELS[trial.attributeIndex] || '',
    'Varyasyon no': Number(trial.variationIndex) || null,
    İfade: trial.stimulus || '',
    Yanıt: trial.response === 'matches' ? 'Eşleşiyor' : trial.response === 'does_not_match' ? 'Eşleşmiyor' : '',
    'Tepki süresi (ms)': trial.rt_ms !== null && trial.rt_ms !== undefined && trial.rt_ms !== '' && Number.isFinite(Number(trial.rt_ms)) ? Number(trial.rt_ms) : null,
    'Zaman aşımı': trial.timed_out === true ? 'Evet' : 'Hayır',
    'Analize dahil': isValidImplicitTrial(trial) ? 'Evet' : 'Hayır',
  }))))

  const methodRows = [
    { Metrik: 'Analiz evreni', Tanım: 'Toplu özetlerde yalnızca rapor durumu Aktif olan tamamlanmış katılımcılar kullanılır. Pasif katılımcılar detay sayfalarında korunur.' },
    { Metrik: 'Geçerli tepki', Tanım: `Yanıtı bulunan, zaman aşımı işaretli olmayan ve tepki süresi ${IMPLICIT_DEADLINE_MS} ms veya altında olan deneme.` },
    { Metrik: 'Ortalama geçerli tepki süresi', Tanım: 'Geçerli denemelerin aritmetik ortalaması. Daha düşük değer daha hızlı tepkiyi gösterir.' },
    { Metrik: 'Medyan geçerli tepki süresi', Tanım: 'Geçerli denemelerin ortanca süresi. Aykırı sürelerden ortalamaya göre daha az etkilenir.' },
    { Metrik: 'Eşleşiyor oranı', Tanım: 'Geçerli denemeler içindeki “Eşleşiyor” yanıtlarının oranı. Daha yüksek değer reklam ile özelliğin daha güçlü ilişkilendirildiğini gösterir.' },
    { Metrik: 'Zamanında yanıt oranı', Tanım: `Yanıtlanan denemeler içinde ${IMPLICIT_DEADLINE_MS} ms sınırında kalan geçerli denemelerin oranı.` },
    { Metrik: 'Sınırlama', Tanım: 'Bu tasarımda kongruent/inkongruent IAT blokları bulunmadığı için IAT D-skoru hesaplanmaz.' },
  ]

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, makeSheet(summaryRows(normalized.map((response) => ({ ...response, advertisement_tests: response.runs }))), [22, 18, 18, 16, 16, 18, 22, 32, 32, 20, 28], ['Zamanında yanıt oranı', 'Eşleşiyor oranı']), 'Özet')
  XLSX.utils.book_append_sheet(workbook, makeSheet(participants, [28, 24, 20, 14, 23, 18, 16, 16, 18, 22, 32, 32, 20, 28], ['Zamanında yanıt oranı', 'Eşleşiyor oranı']), 'Katılımcılar')
  XLSX.utils.book_append_sheet(workbook, makeSheet(advertisementRows, [28, 24, 20, 14, 20, 16, 16, 18, 22, 32, 32, 18, 20, 20, 55, 18, 18, 18, 18, 18, 18, 18], ['Zamanında yanıt oranı', 'Eşleşiyor oranı']), 'Reklam sonuçları')
  XLSX.utils.book_append_sheet(workbook, makeSheet(attributeRows, [28, 24, 14, 20, 14, 25, 60, 16, 18, 32, 32, 18, 20, 22], ['Eşleşiyor oranı', 'Zamanında yanıt oranı']), 'Özellik analizi')
  XLSX.utils.book_append_sheet(workbook, makeSheet(trialRows, [28, 24, 14, 20, 16, 14, 25, 14, 28, 18, 20, 16, 18]), 'Ham denemeler')
  XLSX.utils.book_append_sheet(workbook, makeSheet(methodRows, [34, 110]), 'Metodoloji')
  XLSX.writeFile(workbook, 'eurobank-implicit-detay-raporu.xlsx', { cellDates: true })
}

function questionnaireRows(responses) {
  return responses.flatMap((response) => implicitTestRuns(response).map((run) => {
    const questionnaire = run.questionnaire || {}
    const attitudes = questionnaire.attitudes || {}
    return {
      'Yanıt ID': responseId(response),
      'Katılımcı adı': response.respondent_name || '',
      BFRID: response.bfrid || response.participant_id || '',
      'Rapor durumu': response.reportActive === false ? 'Pasif' : 'Aktif',
      'Tamamlanma zamanı': dateValue(response.completed_at || response.updated_at) || '',
      Reklam: advertisementLabel(run.advertisement_id),
      'Motivasyon skoru (1-5)': scoreValue(questionnaire.motivation),
      'Açık uçlu ana mesaj yanıtı': questionnaire.main_message || '',
      ...Object.fromEntries(IMPLICIT_ATTITUDE_STATEMENTS.map((_, index) => {
        const value = attitudes[index]
        return [`Statement ${index + 1} skoru (1-5)`, scoreValue(value)]
      })),
    }
  }))
}

function questionnaireSummaryRows(detailRows) {
  const activeRows = detailRows.filter((row) => row['Rapor durumu'] === 'Aktif')
  const advertisements = [...new Set(activeRows.map((row) => row.Reklam))]
  const scopes = [
    ['Tüm reklamlar', activeRows],
    ...advertisements.map((advertisement) => [advertisement, activeRows.filter((row) => row.Reklam === advertisement)]),
  ]
  return scopes.map(([scope, rows]) => ({
    Kapsam: scope,
    'Aktif anket yanıtı': rows.length,
    'Aktif katılımcı': new Set(rows.map((row) => row['Yanıt ID']).filter(Boolean)).size,
    'Ortalama motivasyon (1-5)': mean(rows.map((row) => row['Motivasyon skoru (1-5)']), 2),
    ...Object.fromEntries(IMPLICIT_ATTITUDE_STATEMENTS.map((_, index) => [
      `Statement ${index + 1} ortalaması (1-5)`,
      mean(rows.map((row) => row[`Statement ${index + 1} skoru (1-5)`]), 2),
    ])),
  }))
}

export function exportImplicitQuestionnaireExcel(responses) {
  const completedResponses = responses.filter((response) => response.status === 'completed' || implicitTestRuns(response).length)
  const detailRows = questionnaireRows(completedResponses)
  const summaryRows = questionnaireSummaryRows(detailRows)
  const questionRows = [
    { Kod: 'MOTIVATION', 'Cevap türü': '1-5 skor', 'Soru metni': 'Σε ποιο βαθμό αυτή η διαφήμιση θα σας παρακινούσε να αναζητήσετε περισσότερες πληροφορίες για το στεγαστικό δάνειο της Eurobank;' },
    { Kod: 'MAIN_MESSAGE', 'Cevap türü': 'Açık uçlu', 'Soru metni': 'Ποιο θεωρείτε ότι είναι το βασικό μήνυμα που θέλει να σας μεταδώσει αυτή η διαφήμιση;' },
    ...IMPLICIT_ATTITUDE_STATEMENTS.map((statement, index) => ({
      Kod: `STATEMENT_${index + 1}`,
      'Cevap türü': '1-5 skor',
      'Soru metni': statement,
    })),
  ]

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, makeSheet(summaryRows, [22, 20, 18, 30, 30, 30, 30, 30, 30, 30, 30]), 'Özet')
  XLSX.utils.book_append_sheet(workbook, makeSheet(detailRows, [28, 24, 20, 14, 23, 20, 26, 65, 25, 25, 25, 25, 25, 25, 25]), 'Anket yanıtları')
  XLSX.utils.book_append_sheet(workbook, makeSheet(questionRows, [20, 18, 120]), 'Soru tanımları')
  XLSX.writeFile(workbook, 'eurobank-anket-sonuclari.xlsx', { cellDates: true })
}
